import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import Dropdown from "components/CustomDropdown/Dropdown";
import {
  BANNER_AUDIENCES,
  BANNER_STATUSES,
  BANNER_TYPES,
  STATUS_HELP,
} from "Views/Banner/bannerOptions";

const fieldLabel = (text) => (
  <ArgonBox mb={1}>
    <ArgonTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
      {text}
    </ArgonTypography>
  </ArgonBox>
);

function BannerForm({
  heading,
  submitLabel,
  formData,
  setFormData,
  imagePreview,
  imageCaption,
  onImageChange,
  onSubmit,
  onCancel,
  loading,
}) {
  const setField = (name, value) => setFormData((current) => ({ ...current, [name]: value }));

  return (
    <ArgonBox component="form" onSubmit={onSubmit}>
      <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <ArgonBox>
          <ArgonTypography variant="h5" fontWeight="medium">
            {heading}
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text">
            Choose who sees it, and when it should go live.
          </ArgonTypography>
        </ArgonBox>
        <ArgonButton variant="outlined" color="secondary" size="small" onClick={onCancel}>
          Back to List
        </ArgonButton>
      </ArgonBox>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {fieldLabel("Banner image")}
          <ArgonBox
            sx={{
              border: "1px dashed #c7d0dd",
              borderRadius: "12px",
              backgroundColor: "#f8fafc",
              p: 2,
            }}
          >
            <ArgonBox
              component="label"
              htmlFor="banner-image-input"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.8,
                borderRadius: "8px",
                backgroundColor: "#fff",
                border: "1px solid #d2d6da",
                cursor: "pointer",
                width: "fit-content",
              }}
            >
              <ArgonTypography variant="button" fontWeight="medium" color="info">
                Choose image
              </ArgonTypography>
              <ArgonTypography variant="caption" color="text">
                JPG, PNG or WEBP, under 5MB
              </ArgonTypography>
            </ArgonBox>
            <input
              id="banner-image-input"
              type="file"
              accept="image/*"
              onChange={onImageChange}
              style={{ display: "none" }}
            />
            {imagePreview ? (
              <ArgonBox mt={2}>
                <ArgonTypography variant="caption" color="text" display="block" mb={1}>
                  {imageCaption}
                </ArgonTypography>
                <img
                  src={imagePreview}
                  alt="Banner preview"
                  style={{
                    width: "100%",
                    maxHeight: "240px",
                    objectFit: "contain",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    border: "1px solid #e9ecef",
                  }}
                />
              </ArgonBox>
            ) : (
              <ArgonTypography variant="caption" color="text" display="block" mt={1.5}>
                Wide images look best on the tutor and parent home screens.
              </ArgonTypography>
            )}
          </ArgonBox>
        </Grid>

        <Grid item xs={12} md={6}>
          {fieldLabel("Title *")}
          <ArgonInput
            fullWidth
            name="title"
            value={formData.title}
            onChange={(event) => setField("title", event.target.value)}
            placeholder="Enter banner title"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {fieldLabel("Banner type *")}
          <Dropdown
            label=""
            width="100%"
            placeholder="Select type"
            value={formData.bannerType}
            options={BANNER_TYPES}
            onChange={(event) => setField("bannerType", event.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {fieldLabel("Show to *")}
          <Dropdown
            label=""
            width="100%"
            value={formData.audience}
            options={BANNER_AUDIENCES}
            onChange={(event) => setField("audience", event.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {fieldLabel("Status *")}
          <Dropdown
            label=""
            width="100%"
            value={formData.status}
            options={BANNER_STATUSES}
            onChange={(event) => setField("status", event.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <ArgonBox
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              borderRadius: "12px",
              border: "1px solid #e6ebf2",
              backgroundColor: formData.onlyActiveClass ? "#eef6ff" : "#f8fafc",
              p: 1.5,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={Boolean(formData.onlyActiveClass)}
              onChange={(event) => setField("onlyActiveClass", event.target.checked)}
              style={{ marginTop: 3, width: 16, height: 16 }}
            />
            <ArgonBox>
              <ArgonTypography variant="button" fontWeight="medium" color="text">
                Only if their class is running
              </ArgonTypography>
              <ArgonTypography variant="caption" color="text" display="block">
                Tutors see it only when they have an active class. Parents see it only when their child&apos;s class is active. Completed and cancelled classes stay out. Show to still decides tutor, parent, or both.
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        </Grid>

        <Grid item xs={12}>
          <ArgonBox
            sx={{
              borderRadius: "12px",
              backgroundColor: "#f6f8fc",
              border: "1px solid #e6ebf2",
              p: 2,
            }}
          >
            <ArgonTypography variant="body2" color="text">
              {STATUS_HELP[formData.status]}
            </ArgonTypography>
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={6}>
                {fieldLabel(formData.status === "SCHEDULED" ? "Start date and time *" : "Start date and time")}
                <ArgonInput
                  type="datetime-local"
                  fullWidth
                  value={formData.startDate}
                  onChange={(event) => setField("startDate", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {fieldLabel("End date and time")}
                <ArgonInput
                  type="datetime-local"
                  fullWidth
                  value={formData.endDate}
                  onChange={(event) => setField("endDate", event.target.value)}
                />
              </Grid>
            </Grid>
            <ArgonTypography variant="caption" color="text" display="block" mt={1.5}>
              Dates work for pending banners too. A scheduled banner needs a start time. Leave the end empty if it should stay up.
            </ArgonTypography>
          </ArgonBox>
        </Grid>

        <Grid item xs={12}>
          {fieldLabel("Link")}
          <ArgonInput
            fullWidth
            name="redirectUrl"
            value={formData.redirectUrl}
            onChange={(event) => setField("redirectUrl", event.target.value)}
            placeholder="https://example.com/offer"
          />
          <ArgonTypography variant="caption" color="text" display="block" mt={0.75}>
            Optional. The app can open this link when someone taps the banner.
          </ArgonTypography>
        </Grid>
      </Grid>

      <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={4}>
        <ArgonButton variant="outlined" color="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </ArgonButton>
        <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
          {loading ? "Saving..." : submitLabel}
        </ArgonButton>
      </ArgonBox>
    </ArgonBox>
  );
}

BannerForm.propTypes = {
  heading: PropTypes.string.isRequired,
  submitLabel: PropTypes.string.isRequired,
  formData: PropTypes.shape({
    title: PropTypes.string,
    bannerType: PropTypes.string,
    audience: PropTypes.string,
    onlyActiveClass: PropTypes.bool,
    status: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    redirectUrl: PropTypes.string,
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  imagePreview: PropTypes.string,
  imageCaption: PropTypes.string,
  onImageChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default BannerForm;
